/**
 * @file useFontUploadForm.ts
 * @description Состояние окна «Свой шрифт» / «Добавить начертание»
 * (phase19.16 Р1-7; SB99; проект uiux §3–§4): название, вид, источник,
 * галочка права, четыре места начертаний.
 *
 * Файл проверяется сразу при выборе: размер и расширение — здесь, до
 * отправки; остальное — `POST fonts/inspect` (сервер ничего не пишет).
 * После загрузки замечания сервера по файлам (`uploaded[]`), которых не было
 * на проверке, показываются предупреждением.
 * Название и вид шрифта (`categoryGuess` первого проверенного файла)
 * подставляются из файла, пока человек не задал своё. Ответ
 * проверки, пришедший после замены файла, отбрасывается (счётчик на место).
 *
 * Форма не на react-hook-form: поля — 3 строки, а основное состояние —
 * асинхронная проверка файлов по местам; правила готовности — чистая
 * функция `missingUploadSteps` (её же проверяют тесты).
 */

import type { SiteFontCategory } from "@chaiberi/shared";
import { useRef, useState } from "react";

import { useShopFonts } from "../../components/site-view/shop-fonts-context";
import { useNotify } from "../../components/ui/NotifyProvider";
import {
  familyTakenText,
  fontErrorCode,
  fontErrorText,
  INSPECT_NETWORK_TEXT,
  isFontNetworkError,
} from "../../lib/site-view/shop-font-errors";
import {
  categoryFromDto,
  categoryToDto,
  checkFontFile,
  FONT_SLOTS,
  lateUploadWarnings,
  loadedSlots,
  missingUploadSteps,
  slotOf,
  type FontInspectResult,
  type FontSlotKey,
  type ShopFontDto,
} from "../../lib/site-view/shop-fonts";

export type SlotState =
  | { status: "empty" }
  | { status: "checking"; file: File }
  | { status: "ready"; file: File; info: FontInspectResult; keepAsIs: boolean }
  | { status: "error"; fileName: string; message: string };

type Slots = Record<FontSlotKey, SlotState>;
const EMPTY_SLOTS: Slots = { regular: { status: "empty" }, bold: { status: "empty" }, italic: { status: "empty" }, boldItalic: { status: "empty" } };

interface Options {
  /** Шрифт для догрузки начертания; `null` — новый шрифт. */
  font: ShopFontDto | null;
  defaultCategory: SiteFontCategory;
  onDone: (font: ShopFontDto) => void;
}

export function useFontUploadForm({ font, defaultCategory, onDone }: Options) {
  const shop = useShopFonts();
  const notify = useNotify();
  const mode = font ? "addFile" : "create";
  const [family, setFamilyRaw] = useState(font?.family ?? "");
  const [category, setCategoryRaw] = useState<SiteFontCategory>(defaultCategory);
  const [sourceNote, setSourceNote] = useState("");
  const [consent, setConsent] = useState(false);
  const [slots, setSlots] = useState<Slots>(EMPTY_SLOTS);
  const [submitting, setSubmitting] = useState(false);
  const [familyError, setFamilyError] = useState<string | null>(null);
  const [networkError, setNetworkError] = useState(false);
  const tokens = useRef<Record<string, number>>({});
  // Название, подставленное из файла: пока человек его не менял, следующий файл может заменить.
  const autoFamily = useRef(family);
  const familyRef = useRef(family);
  familyRef.current = family;
  // Вид шрифта выбран человеком или уже подставлен из файла — больше не трогаем.
  const categoryFixed = useRef(false);

  const loaded = font ? loadedSlots(font) : [];
  const setSlot = (key: FontSlotKey, state: SlotState) => setSlots((prev) => ({ ...prev, [key]: state }));
  const setFamily = (value: string) => {
    setFamilyRaw(value);
    setFamilyError(null);
  };
  const setCategory = (value: SiteFontCategory) => {
    categoryFixed.current = true;
    setCategoryRaw(value);
  };

  async function pick(key: FontSlotKey, file: File): Promise<void> {
    const token = (tokens.current[key] ?? 0) + 1;
    tokens.current[key] = token;
    const local = checkFontFile(file);
    if (local) return setSlot(key, { status: "error", fileName: file.name, message: local });
    setSlot(key, { status: "checking", file });
    try {
      const info = await shop.inspect(file);
      if (tokens.current[key] !== token) return;
      setSlot(key, { status: "ready", file, info, keepAsIs: false });
      if (!font && info.familyName && familyRef.current === autoFamily.current) {
        autoFamily.current = info.familyName;
        setFamilyRaw(info.familyName);
      }
      if (!font && !categoryFixed.current && info.categoryGuess) {
        categoryFixed.current = true;
        setCategoryRaw(categoryFromDto(info.categoryGuess));
      }
    } catch (err) {
      if (tokens.current[key] !== token) return;
      const message = isFontNetworkError(err) ? INSPECT_NETWORK_TEXT : fontErrorText(err, "Не удалось проверить файл.");
      setSlot(key, { status: "error", fileName: file.name, message });
    }
  }

  const clear = (key: FontSlotKey) => {
    tokens.current[key] = (tokens.current[key] ?? 0) + 1;
    setSlot(key, { status: "empty" });
  };
  const move = (from: FontSlotKey, to: FontSlotKey) =>
    setSlots((prev) => ({ ...prev, [to]: prev[from], [from]: { status: "empty" } }));
  const keep = (key: FontSlotKey) =>
    setSlots((prev) => {
      const slot = prev[key];
      return slot.status === "ready" ? { ...prev, [key]: { ...slot, keepAsIs: true } } : prev;
    });

  const ready = FONT_SLOTS.filter((s) => slots[s.key].status === "ready" && !loaded.includes(s.key));
  const checking = FONT_SLOTS.some((s) => slots[s.key].status === "checking");
  const missing = missingUploadSteps({
    mode,
    family,
    hasRequiredFile: mode === "create" ? slots.regular.status === "ready" : ready.length > 0,
    consent,
  });
  const canSubmit = missing.length === 0 && !checking && !submitting && !familyError;
  /** В окне есть выбранные файлы — Escape возвращает к каталогу, а не закрывает окно. */
  const hasFiles = FONT_SLOTS.some((s) => slots[s.key].status !== "empty");

  async function submit(): Promise<void> {
    if (!canSubmit) return;
    setSubmitting(true);
    setNetworkError(false);
    // Итоги проверки по местам — до отправки: после догрузки места очищаются.
    const checked = (key: FontSlotKey): FontInspectResult | null => {
      const slot = slots[key];
      return slot.status === "ready" ? slot.info : null;
    };
    const warnings: string[] = [];
    try {
      let result: ShopFontDto | null = null;
      if (!font) {
        const files: Record<string, File> = {};
        for (const s of ready) {
          const slot = slots[s.key];
          if (slot.status === "ready") files[s.field] = slot.file;
        }
        const created = await shop.create({ family: family.trim(), category: categoryToDto(category), sourceNote: sourceNote.trim() || null, files });
        result = created;
        notify.success(`Шрифт «${created.family}» загружен`);
        warnings.push(...lateUploadWarnings(created.uploaded ?? [], checked));
      } else {
        for (const s of ready) {
          const slot = slots[s.key];
          if (slot.status !== "ready") continue;
          const added = await shop.addFile(font.id, { file: slot.file, weight: s.weight, style: s.style });
          result = added;
          // В ответе могут быть и прежние файлы — замечания берём только по этому месту.
          warnings.push(...lateUploadWarnings((added.uploaded ?? []).filter((u) => slotOf(u.weight, u.style) === s.key), checked));
          // Дошедшее начертание из окна убираем: повтор после сбоя его не шлёт.
          setSlot(s.key, { status: "empty" });
        }
        if (result) notify.success(`Начертания добавлены в «${result.family}»`);
      }
      if (warnings.length > 0) notify.warning(warnings.join(" "));
      if (result) onDone(result);
    } catch (err) {
      if (isFontNetworkError(err)) setNetworkError(true);
      else if (fontErrorCode(err) === "FONT_FAMILY_TAKEN") setFamilyError(familyTakenText(family.trim()));
      else notify.error(fontErrorText(err, "Не удалось загрузить шрифт."));
    } finally {
      setSubmitting(false);
    }
  }

  // Группы: поля формы · места начертаний · отправка.
  // prettier-ignore
  return {
    mode, family, setFamily, category, setCategory, sourceNote, setSourceNote, consent, setConsent,
    slots, loaded, pick, clear, move, keep, hasFiles,
    missing, canSubmit, submitting, familyError, networkError, submit,
  };
}

export type FontUploadForm = ReturnType<typeof useFontUploadForm>;
